<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                >Search Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="pa-2">

                    <v-col lg="2" md="3" cols="12">
                      <v-select
                        v-model="year.value.value"
                        :items="yearOptions"
                        label="Year"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :error-messages="year.errorMessage.value"
                        clearable
                        hide-details="auto"
                      />
                    </v-col>

                    <v-col lg="3" md="3" cols="12">
                      <v-date-input
                        label="From Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-start"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="from_date.value.value"
                        :error-messages="from_date.errorMessage.value"
                        autocomplete="off"
                        hide-details
                        clearable
                      ></v-date-input>
                    </v-col>

                    <v-col lg="3" md="3" cols="12">
                      <v-date-input
                        label="To Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-end"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="to_date.value.value"
                        :error-messages="to_date.errorMessage.value"
                        autocomplete="off"
                        hide-details
                        clearable
                      ></v-date-input>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-btn
                        class="bg-brand w-15"
                        type="submit"
                        ref="searchButton"
                        >Search</v-btn
                      >
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid style="margin-top: -20px;">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Depreciation Schedule Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                
                <!-- Summary Cards -->
                <v-row class="py-2" v-if="summary.total_records > 0">
                  <v-col cols="12">
                    <v-card class="pa-4 bg-blue-grey-lighten-5">
                      <v-row>
                        <v-col md="4" cols="12">
                          <div class="text-subtitle-2 text-grey-darken-1">Total Depreciation Records</div>
                          <div class="text-h5 font-weight-bold text-primary">{{ summary.total_records }}</div>
                        </v-col>
                        <v-col md="4" cols="12">
                          <div class="text-subtitle-2 text-grey-darken-1">Total Assets Depreciated</div>
                          <div class="text-h5 font-weight-bold text-success">{{ summary.total_assets }}</div>
                        </v-col>
                        <v-col md="4" cols="12">
                          <div class="text-subtitle-2 text-grey-darken-1">Total Depreciation Amount</div>
                          <div class="text-h5 font-weight-bold text-error">{{ formatCurrency(summary.total_depreciation) }}</div>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row class="py-2">
                  
                  <v-col md="3" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="6" cols="12">
                    <div class="d-flex justify-center">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          :disabled="reports.length === 0"
                          >CSV</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          :disabled="reports.length === 0"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          :disabled="reports.length === 0"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12" class="d-flex align-center justify-end">
                    <v-btn
                      v-if="reports.length > 0"
                      variant="outlined"
                      size="small"
                      @click="expandAll"
                      prepend-icon="mdi-arrow-expand-all"
                    >
                      {{ allExpanded ? 'Collapse All' : 'Expand All' }}
                    </v-btn>
                  </v-col>

                </v-row>

                <!-- Grouped by Depreciation Master -->
                <v-expansion-panels v-model="expandedPanels" multiple class="mt-2">
                  <v-expansion-panel
                    v-for="(master, index) in reports"
                    :key="master.id"
                    class="mb-2"
                  >
                    <v-expansion-panel-title class="bg-blue-grey-lighten-5">
                      <v-row no-gutters align="center">
                        <v-col cols="12" md="2">
                          <div class="text-caption text-grey-darken-1">Depreciation Date</div>
                          <div class="font-weight-bold">{{ master.depreciation_date }}</div>
                        </v-col>
                        <v-col cols="12" md="2" v-if="master.code">
                          <div class="text-caption text-grey-darken-1">Code</div>
                          <div class="font-weight-medium">{{ master.code }}</div>
                        </v-col>
                        <v-col cols="12" md="3">
                          <div class="text-caption text-grey-darken-1">Total Depreciation</div>
                          <div class="font-weight-bold text-error">{{ formatCurrency(master.total_depreciation_amount) }}</div>
                        </v-col>
                        <v-col cols="12" md="2">
                          <div class="text-caption text-grey-darken-1">Assets Count</div>
                          <div class="font-weight-medium">
                            <v-chip size="small" color="primary">{{ master.details.length }}</v-chip>
                          </div>
                        </v-col>
                        <v-col cols="12" md="3" class="text-right">
                          <v-chip size="small" color="success" variant="outlined">
                            <v-icon start size="small">mdi-file-document-outline</v-icon>
                            View Details
                          </v-chip>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <v-data-table
                        :headers="detailHeaders"
                        :items="master.details"
                        :search="search"
                        :hover="true"
                        density="compact"
                        hide-default-footer
                        :items-per-page="-1"
                        class="elevation-1"
                      >
                        <template v-slot:item.index="{ index }">
                          {{ ++index }}
                        </template>

                        <template v-slot:item.depreciation_rate="{ item }">
                          <v-chip size="small" color="info" variant="outlined">
                            {{ Number(item.depreciation_rate).toFixed(2) }}%
                          </v-chip>
                        </template>

                        <template v-slot:item.depreciation_amount="{ item }">
                          <span class="font-weight-medium">{{ formatCurrency(item.depreciation_amount) }}</span>
                        </template>

                        <template v-slot:item.accumulated_depreciation="{ item }">
                          <span class="text-warning">{{ formatCurrency(item.accumulated_depreciation) }}</span>
                        </template>

                        <template v-slot:item.net_book_value="{ item }">
                          <span class="text-success font-weight-bold">{{ formatCurrency(item.net_book_value) }}</span>
                        </template>

                      </v-data-table>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>

                <!-- No Data Message -->
                <v-row v-if="!loading && reports.length === 0" class="mt-4">
                  <v-col cols="12" class="text-center">
                    <v-icon size="64" color="grey">mdi-file-document-outline</v-icon>
                    <p class="text-h6 text-grey mt-2">No depreciation records found</p>
                  </v-col>
                </v-row>

              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref, getCurrentInstance } from "vue";
import { toast } from "vue3-toastify";
import Axios from "axios";
import { prefix } from "@/store/api";

export default {
  components: {},
  data() {
    // Generate year options (from 2020 to current year + 5)
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2020; year <= currentYear + 5; year++) {
      years.push(year);
    }
    const yearOptions = years.reverse(); // Show most recent years first

    return {
      itemsPerPage: 10,
      panel: [0],
      expandedPanels: [],
      allExpanded: false,
      search: "",
      dialog: false,
      data: {},
      yearOptions,
      detailHeaders: [
        { title: "#SL", align: "center", key: "index", sortable: false, width: "60px"},
        { title: "Asset Code", key: "asset_code", align: "center", sortable: false },
        { title: "Asset Name", key: "asset_name", align: "center", sortable: false },
        { title: "Depreciation Rate", key: "depreciation_rate", align: "center", sortable: false },
        { title: "Depreciation Amount", key: "depreciation_amount", align: "center", sortable: false },
        { title: "Accumulated Depreciation", key: "accumulated_depreciation", align: "center", sortable: false },
        { title: "Net Book Value", key: "net_book_value", align: "center", sortable: false },
      ],
    };
  },
  computed: {},
  watch: {},
  created() {
  },
  computed: {
  },
  setup(props, { expose }) {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        year: yup.number().positive().integer().min(1900).max(2100).nullable().label('Year'),
        from_date: yup.date().nullable().label('From Date'),
        to_date: yup.date().nullable().label('To Date'),
      }),
    });

    const year = useField("year");
    const from_date = useField("from_date");
    const to_date = useField("to_date");
    const errorMessages = ref("");
    const loading = ref(false);
    const store = useStore();
    const reports = ref([]);
    const summary = ref({
      total_records: 0,
      total_depreciation: 0,
      total_assets: 0,
    });

    const submit = handleSubmit((values) => {
      loading.value = true;

      // Format dates if they exist
      const params = {
        year: values.year || null,
        from_date: from_date.value.value ? new Date(from_date.value.value).toISOString().split('T')[0] : null,
        to_date: to_date.value.value ? new Date(to_date.value.value).toISOString().split('T')[0] : null,
      };

      Axios.get(`${prefix}assets/reports/depreciation-schedule`, { params })
        .then((response) => {
          if (response.data.status) {
            reports.value = response.data.data || [];
            
            // Update summary
            if (response.data.summary) {
              summary.value = response.data.summary;
            }
            
            toast.success(response.data.message || 'Data loaded successfully');
          } else {
            toast.error(response.data.message || 'Failed to load data');
            reports.value = [];
          }
          loading.value = false;
        })
        .catch((error) => {
          loading.value = false;
          reports.value = [];
          console.error('Depreciation schedule error:', error);
          const errorMsg = error.response?.data?.message || error.message || 'An error occurred while loading data';
          toast.error(errorMsg);
        });
    });

    const formatCurrency = (val) => Number(val || 0).toLocaleString('en-BD', { minimumFractionDigits: 2 });

    return {
      year,
      from_date,
      to_date,
      submit,
      errorMessages,
      loading,
      reports,
      summary,
      formatCurrency,
    };
  },
  methods: {
    expandAll() {
      if (this.allExpanded) {
        this.expandedPanels = [];
      } else {
        this.expandedPanels = this.reports.map((_, index) => index);
      }
      this.allExpanded = !this.allExpanded;
    },
    goTo(val) {
      this.$router.push({ path: val }).catch((err) => {});
    },

    exportToPDF() {
      let serialNumber = 1;
      const columns = [
        "#SL",
        "Depreciation Date",
        "Asset Name",
        "Depreciation Rate",
        "Depreciation Amount",
        "Accumulated Depreciation",
        "Net Book Value",
      ];
      const rows = [];
      
      (this.reports || []).forEach((master) => {
        master.details.forEach((detail) => {
          rows.push([
            serialNumber++,
            master.depreciation_date,
            detail.asset_name,
            Number(detail.depreciation_rate).toFixed(2) + '%',
            this.formatCurrency(detail.depreciation_amount),
            this.formatCurrency(detail.accumulated_depreciation),
            this.formatCurrency(detail.net_book_value),
          ]);
        });
      });

      this.$exportTableToPDF(
        columns,
        rows,
        "Depreciation Schedule Report",
        "depreciation_schedule_report_" + this.$fileDate()
      );
    },
    exportToExcel() {
      var data = [[
        "#SL",
        "Depreciation Date",
        "Asset Name",
        "Depreciation Rate",
        "Depreciation Amount",
        "Accumulated Depreciation",
        "Net Book Value",
      ]];
      var serialNumber = 1;
      
      (this.reports || []).forEach((master) => {
        master.details.forEach((detail) => {
          data.push([
            serialNumber++,
            master.depreciation_date,
            detail.asset_name,
            Number(detail.depreciation_rate).toFixed(2),
            detail.depreciation_amount,
            detail.accumulated_depreciation,
            detail.net_book_value,
          ]);
        });
      });

      this.$exportTableToExcel(data, "depreciation_schedule_report_" + this.$fileDate());
    },
    exportToCSV() {
      var data = [[
        "#SL",
        "Depreciation Date",
        "Asset Name",
        "Depreciation Rate",
        "Depreciation Amount",
        "Accumulated Depreciation",
        "Net Book Value",
      ]];
      var serialNumber = 1;
      
      (this.reports || []).forEach((master) => {
        master.details.forEach((detail) => {
          data.push([
            serialNumber++,
            master.depreciation_date,
            detail.asset_name,
            Number(detail.depreciation_rate).toFixed(2),
            detail.depreciation_amount,
            detail.accumulated_depreciation,
            detail.net_book_value,
          ]);
        });
      });

      this.$exportTableToCSV(data, "depreciation_schedule_report_" + this.$fileDate());
    },
  },
  mounted() {
    // Data will load only when user clicks Search button
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

